import { useCallback, useEffect, useState } from "react";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetSalesforceListSyncTriggerQuery,
	useGetSalesforceOAuthStatusQuery,
	useUpdateSalesforceListSyncTriggerMutation,
} from "@/pages/Private/redux/salesforce/salesforce.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { SalesforceSyncTriggerWithInherit } from "../../../../Company/schema/salesforce";

interface UseSalesforceSyncTriggerOptions {
	listId: number;
}

/**
 * The list's Salesforce **record sync** trigger. There is deliberately no opportunity
 * counterpart: `salesforce_deal_creation_trigger` is keyed by `companyId` alone, so the
 * opportunity trigger is a company setting and lives in the Salesforce settings tab.
 */
export const useSalesforceSyncTrigger = ({ listId }: UseSalesforceSyncTriggerOptions) => {
	const isSalesforceEnabled = useIsFeatureActive(FeaturesCodes.SALESFORCE_CRM);
	const { data: oauthStatus } = useGetSalesforceOAuthStatusQuery(undefined, {
		skip: !isSalesforceEnabled,
	});
	const isConnected = oauthStatus?.connected ?? false;

	const { data: listSyncTrigger } = useGetSalesforceListSyncTriggerQuery(listId, {
		skip: !isSalesforceEnabled || !isConnected || !listId,
	});
	const [updateListSyncTrigger] = useUpdateSalesforceListSyncTriggerMutation();

	const [selectedSalesforceSyncTrigger, setSelectedSalesforceSyncTrigger] = useState<
		SalesforceSyncTriggerWithInherit | undefined
	>(undefined);

	useEffect(() => {
		if (listSyncTrigger) {
			setSelectedSalesforceSyncTrigger(listSyncTrigger.trigger as SalesforceSyncTriggerWithInherit);
		}
	}, [listSyncTrigger]);

	const hasSyncTriggerChanged = useCallback(
		() =>
			isSalesforceEnabled &&
			isConnected &&
			selectedSalesforceSyncTrigger !== undefined &&
			selectedSalesforceSyncTrigger !== listSyncTrigger?.trigger,
		[isSalesforceEnabled, isConnected, selectedSalesforceSyncTrigger, listSyncTrigger?.trigger]
	);

	const saveSyncTrigger = useCallback(async () => {
		if (hasSyncTriggerChanged() && selectedSalesforceSyncTrigger) {
			await updateListSyncTrigger({
				listId,
				data: { trigger: selectedSalesforceSyncTrigger },
			}).unwrap();
		}
	}, [hasSyncTriggerChanged, selectedSalesforceSyncTrigger, listId, updateListSyncTrigger]);

	return {
		isSalesforceEnabled,
		isConnected,

		listSyncTrigger,
		selectedSalesforceSyncTrigger,
		setSelectedSalesforceSyncTrigger,

		hasSyncTriggerChanged,
		saveSyncTrigger,
	};
};
